import { useUnits } from '../../units/UnitContext';
import { formatDualDimension } from '../../units/unitConversions';

const VIEWBOX_WIDTH = 1100;
const VIEWBOX_HEIGHT = 460;
const SCREEN_PITCH = 160;
const PROFILE_REPEATS = 3;
const PLOT = Object.freeze({ left: 250, top: 104 });

const finiteNumber = value => value !== null && value !== '' && Number.isFinite(Number(value));
const coordinate = value => Number(value.toFixed(3));

const linePath = (points, xFor, yFor, radiusKey) => points
  .map((point, index) => `${index === 0 ? 'M' : 'L'}${xFor(point.axialMm)} ${yFor(point[radiusKey])}`)
  .join(' ');

const repeatProfilePoints = (points, periodMm) =>
  Array.from({ length: PROFILE_REPEATS }, (_, repeatIndex) => points
    .filter((_, pointIndex) => repeatIndex === 0 || pointIndex > 0)
    .map(point => ({
      ...point,
      axialMm: Number(point.axialMm) + repeatIndex * periodMm,
    })))
    .flat();

const regionPaths = (points, region, xFor, yFor, originalY) => {
  const paths = [];
  let group = [];
  const flush = () => {
    if (group.length > 1) {
      const estimated = group.map((point, index) =>
        `${index === 0 ? 'M' : 'L'}${xFor(point.axialMm)} ${yFor(point.estimatedRadiusMm)}`
      ).join(' ');
      paths.push(`${estimated} L${xFor(group.at(-1).axialMm)} ${originalY} L${xFor(group[0].axialMm)} ${originalY} Z`);
    }
    group = [];
  };
  points.forEach(point => {
    if (point.materialRegion === region) group.push(point);
    else flush();
  });
  flush();
  return paths;
};

const FormedThreadProfile = ({ profile, designation }) => {
  const { unitSystem } = useUnits();
  if (profile?.status !== 'AVAILABLE') {
    return (
      <div className="formedProfileUnavailable" role="status">
        The estimated formed-thread geometric illustration is unavailable for this selected diameter.
      </div>
    );
  }

  const points = Array.isArray(profile.profilePoints) ? profile.profilePoints : [];
  const startMm = Number(profile.coordinateSystem?.periodStartMm);
  const endMm = Number(profile.coordinateSystem?.periodEndMm);
  const originalRadiusMm = Number(profile.originalHole?.radiusMm);
  const d1MinimumRadiusMm = Number(profile.referenceTolerance?.finishedMinorDiameterD1LimitsMm?.minimumMm) / 2;
  const d1MaximumRadiusMm = Number(profile.referenceTolerance?.finishedMinorDiameterD1LimitsMm?.maximumMm) / 2;
  const valid = points.length >= 2
    && finiteNumber(startMm) && finiteNumber(endMm) && endMm > startMm
    && finiteNumber(originalRadiusMm)
    && finiteNumber(d1MinimumRadiusMm) && finiteNumber(d1MaximumRadiusMm)
    && finiteNumber(profile.coordinateSystem?.crestCenterAxialMm)
    && points.every(point => finiteNumber(point.axialMm)
      && finiteNumber(point.referenceRadiusMm)
      && finiteNumber(point.estimatedRadiusMm))
    && points.every((point, index) => index === 0 || Number(point.axialMm) > Number(points[index - 1].axialMm));

  if (!valid) {
    return (
      <div className="formedProfileUnavailable" role="status">
        The supplied formed-thread geometry does not contain valid drawable coordinates.
      </div>
    );
  }

  const periodMm = endMm - startMm;
  const scale = SCREEN_PITCH / periodMm;
  const repeatedPoints = repeatProfilePoints(points, periodMm);
  const repeatedEndMm = startMm + periodMm * PROFILE_REPEATS;
  const radii = points.flatMap(point => [Number(point.referenceRadiusMm), Number(point.estimatedRadiusMm)]);
  radii.push(originalRadiusMm, d1MinimumRadiusMm, d1MaximumRadiusMm);
  const maximumRadiusMm = Math.max(...radii);
  const xFor = axialMm => coordinate(PLOT.left
    + (Number(axialMm) - startMm) * scale);
  // Engineering radii increase away from the axis; the upper-half section therefore maps
  // larger radii upward while SVG screen Y increases downward.
  // The same pixels-per-millimetre scale is used on both axes so the 60° profile is
  // not stretched horizontally or vertically.
  const yFor = radiusMm => coordinate(PLOT.top + (maximumRadiusMm - Number(radiusMm)) * scale);
  const originalY = yFor(originalRadiusMm);
  const estimatedPath = linePath(repeatedPoints, xFor, yFor, 'estimatedRadiusMm');
  const referencePath = linePath(repeatedPoints, xFor, yFor, 'referenceRadiusMm');
  const materialPath = `${estimatedPath} L${xFor(repeatedEndMm)} 58 L${xFor(startMm)} 58 Z`;
  const displacedPaths = regionPaths(repeatedPoints, 'DISPLACED_FROM_INITIAL_BOUNDARY', xFor, yFor, originalY);
  const accumulatedPaths = regionPaths(repeatedPoints, 'ACCUMULATED_TOWARD_CREST', xFor, yFor, originalY);
  const d1MinimumY = yFor(d1MinimumRadiusMm);
  const d1MaximumY = yFor(d1MaximumRadiusMm);
  const crestCenterAxialMm = Number(profile.coordinateSystem?.crestCenterAxialMm) + periodMm;
  const centerPoint = repeatedPoints.reduce((closest, point) => (
    Math.abs(point.axialMm - crestCenterAxialMm) < Math.abs(closest.axialMm - crestCenterAxialMm)
      ? point : closest
  ), repeatedPoints[0]);
  const seamCenters = Array.from({ length: PROFILE_REPEATS }, (_, repeatIndex) => {
    const axialMm = Number(profile.coordinateSystem?.crestCenterAxialMm) + repeatIndex * periodMm;
    const point = repeatedPoints.reduce((closest, candidate) => (
      Math.abs(candidate.axialMm - axialMm) < Math.abs(closest.axialMm - axialMm)
        ? candidate : closest
    ), repeatedPoints[0]);
    return { axialMm, radiusMm: Number(point.estimatedRadiusMm) };
  });
  const diameterLabel = value => formatDualDimension(value, unitSystem).primary;

  return (
    <section className="formedThreadProfile" aria-labelledby="formed-profile-heading">
      <div className="formedProfileHeading">
        <div>
          <p>Simplified volume-conservation model</p>
          <h5 id="formed-profile-heading">Estimated formed-thread profile</h5>
        </div>
        <span>Internal thread</span>
      </div>
      <div className="formedProfileCanvas">
        <svg
          viewBox={`0 0 ${VIEWBOX_WIDTH} ${VIEWBOX_HEIGHT}`}
          role="img"
          aria-labelledby="formed-profile-title formed-profile-description"
          data-coordinate-units={profile.coordinateSystem?.units}
          data-radial-screen-direction="up"
          data-profile-repeats={PROFILE_REPEATS}
          data-engineering-scale-mode="isotropic"
          data-axial-pixels-per-mm={coordinate(scale)}
          data-radial-pixels-per-mm={coordinate(scale)}
        >
          <title id="formed-profile-title">Estimated formed internal thread profile for {designation}</title>
          <desc id="formed-profile-description">
            Three-pitch upper-half radial section. Axial position runs horizontally, engineering radius increases upward, material lies outside the bore, and each pair of formed lips joins at a central section seam to the original hole boundary.
          </desc>
          <defs>
            <marker id="formed-profile-arrow" viewBox="0 0 10 10" refX="5" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
              <path className="formedProfileArrow" d="M0 0 L10 5 L0 10 Z" />
            </marker>
            <pattern id="formed-profile-material" width="12" height="12" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
              <line className="formedProfileHatch" x1="0" y1="0" x2="0" y2="12" />
            </pattern>
          </defs>

          <path className="formedProfileMaterial" d={materialPath} data-testid="formed-profile-material" />
          <path className="formedProfileMaterialHatch" d={materialPath} fill="url(#formed-profile-material)" />
          {displacedPaths.map((path, index) => <path key={`displaced-${index}`} className="formedProfileRegion is-displaced" d={path} data-material-region="displaced" />)}
          {accumulatedPaths.map((path, index) => <path key={`accumulated-${index}`} className="formedProfileRegion is-accumulated" d={path} data-material-region="accumulated" />)}

          <line className="formedProfileOriginalHole" x1={xFor(startMm)} y1={originalY} x2={xFor(repeatedEndMm)} y2={originalY} data-testid="formed-profile-original-hole" data-radius-mm={originalRadiusMm} />
          <path className="formedProfileReference" d={referencePath} data-testid="formed-profile-reference" />
          <path className="formedProfileEstimated" d={estimatedPath} data-testid="formed-profile-estimated" data-profile-point-count={points.length} data-rendered-profile-repeats={PROFILE_REPEATS} />
          <g className="formedProfileSeams" data-testid="formed-profile-central-seams">
            {seamCenters.map(seam => (
              <line
                key={seam.axialMm}
                className="formedProfileSeam"
                x1={xFor(seam.axialMm)}
                y1={originalY}
                x2={xFor(seam.axialMm)}
                y2={yFor(seam.radiusMm)}
                data-axial-mm={seam.axialMm}
              />
            ))}
          </g>

          <g className="formedProfileD1References" data-testid="formed-profile-d1-references">
            <line x1="220" y1={d1MinimumY} x2="770" y2={d1MinimumY} data-diameter-mm={profile.referenceTolerance.finishedMinorDiameterD1LimitsMm.minimumMm} data-radius-mm={d1MinimumRadiusMm} />
            <line x1="220" y1={d1MaximumY} x2="770" y2={d1MaximumY} data-diameter-mm={profile.referenceTolerance.finishedMinorDiameterD1LimitsMm.maximumMm} data-radius-mm={d1MaximumRadiusMm} />
            <line className="formedProfileD1Leader" x1="770" y1={d1MaximumY} x2="800" y2="232" />
            <line className="formedProfileD1Leader" x1="770" y1={d1MinimumY} x2="800" y2="267" />
            <text x="815" y="228">D1 max = {diameterLabel(profile.referenceTolerance.finishedMinorDiameterD1LimitsMm.maximumMm)}</text>
            <text x="815" y="263">D1 min = {diameterLabel(profile.referenceTolerance.finishedMinorDiameterD1LimitsMm.minimumMm)}</text>
          </g>

          <g className="formedProfileCrestCallout" data-testid="formed-profile-incomplete-crest">
            <line x1={xFor(centerPoint.axialMm)} y1={yFor(centerPoint.estimatedRadiusMm)} x2="620" y2="72" />
            <text x="635" y="68">Formed crest seam</text>
          </g>

          <g className="formedProfileAxis">
            <line x1={xFor(startMm + periodMm)} y1="306" x2={xFor(startMm + periodMm * 2)} y2="306" />
            <line className="formedProfileDimension" x1={xFor(startMm + periodMm) + 8} y1="306" x2={xFor(startMm + periodMm * 2) - 8} y2="306" />
            <text x={xFor(startMm + periodMm * 1.5)} y="334" textAnchor="middle">P = {diameterLabel(periodMm)}</text>
          </g>

          <g className="formedProfileLegend" aria-label="Drawing legend">
            <line className="formedProfileLegendOriginal" x1="82" y1="375" x2="126" y2="375" />
            <text x="136" y="380">Original pre-drilled hole</text>
            <line className="formedProfileLegendReference" x1="350" y1="375" x2="394" y2="375" />
            <text x="404" y="380">Reference ISO profile</text>
            <line className="formedProfileLegendEstimated" x1="610" y1="375" x2="654" y2="375" />
            <text x="664" y="380">Estimated formed profile</text>
            <rect className="formedProfileLegendDisplaced" x="82" y="405" width="44" height="12" />
            <text x="136" y="416">Displaced region</text>
            <rect className="formedProfileLegendAccumulated" x="350" y="405" width="44" height="12" />
            <text x="404" y="416">Accumulated region</text>
          </g>
        </svg>
      </div>
      <p className="formedProfileExplanation">
        Estimated formed-thread geometry based on a simplified material volume-conservation model. Actual results depend on material, forming-tap geometry and process conditions. The illustration does not prove finished-thread tolerance conformance.
      </p>
    </section>
  );
};

export default FormedThreadProfile;
